import { Button } from '@seiue/ui'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import { QuestionnaireContextParam } from './types'
import { DragContext, QuestionnaireContext } from './context'
import { isArray } from '@seiue/util'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { BaseItem } from './BaseItem'
import { SelectComponent } from './SelectComponent'
import { addSerialNumber } from './utils'
import { getTheme } from '@/utils/theme'
const theme = getTheme()

/**
 * 问卷结构显示区域
 *
 * @param param - 参数
 * @param param.goBack - 返回上一步
 * @returns React.ReactNode
 */
export const EditArea: React.FC<{
  goBack?: () => void
}> = ({ goBack }) => {
  const { task, questionnaires, setIsPreview } =
    useContext<QuestionnaireContextParam>(QuestionnaireContext)
  const [dragDisabled, setDragDisabled] = useState<boolean>(false)
  if (!task) return null
  return (
    <Wrapper>
      <DragContext.Provider
        value={{
          dragDisabled: dragDisabled,
          onChangeDragDisabled: (status) => {
            setDragDisabled(status)
          },
        }}
      >
        <MainWrapper>
          <TitleArea>
            <Title>{task.name}</Title>
            <Description>{task.description}</Description>
          </TitleArea>
          <Droppable droppableId="form">
            {(provided) => {
              return (
                <TargetAreaWrapper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {!isArray(questionnaires) || questionnaires.length === 0 ? (
                    <TargetArea>点击左侧题型或拖拽题型到此区域</TargetArea>
                  ) : (
                    <FormWrapper>
                      {addSerialNumber(questionnaires).map(
                        (questionnaire, index) => {
                          return (
                            <Draggable
                              draggableId={`${index}`}
                              index={index}
                              key={`${
                                questionnaire.id || questionnaire.uuid || index
                              }`}
                              isDragDisabled={dragDisabled}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <BaseItem
                                    serialNumber={questionnaire.serialNumber}
                                    isDrag={snapshot.isDragging}
                                    currentIndex={index}
                                    content={
                                      <SelectComponent
                                        questionnaire={questionnaire}
                                        currentIndex={index}
                                      />
                                    }
                                    questionnaire={questionnaire}
                                  />
                                </div>
                              )}
                            </Draggable>
                          )
                        }
                      )}
                      {provided.placeholder}
                    </FormWrapper>
                  )}
                </TargetAreaWrapper>
              )
            }}
          </Droppable>
        </MainWrapper>
        <BottomWrapper>
          <Bottom>
            <Button onClick={goBack}>
              <GrowpIcon
                name="LeftArrow"
                text="上一步"
                size={14}
                color={theme.text._2}
              />
            </Button>
            <Button onClick={() => setIsPreview(true)}>
              <GrowpIcon
                name="Eye"
                text="预览"
                size={14}
                color={theme.text._2}
              />
            </Button>
          </Bottom>
        </BottomWrapper>
      </DragContext.Provider>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 760px;
  min-width: 760px;
  height: 100%;
`

const MainWrapper = styled.div`
  width: 760px;
  min-height: 294px;
  margin-top: 20px;
  border-radius: 8px;
  padding: 40px 0 40px 0;
  background-color: #ffffff;
  box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.2),
    0px 3px 8px -1px rgba(21, 29, 41, 0.04);
`

const BottomWrapper = styled.div`
  padding-bottom: 20px;
`

const Bottom = styled.div`
  width: 760px;
  height: 72px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 20px 278px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.2),
    0px 3px 8px -1px rgba(21, 29, 41, 0.04);
  & .se-button__default {
    width: 96px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const TitleArea = styled.div`
  width: 760px;
  min-height: 70px;
  padding: 0 40px 0 40px;
`

const TargetArea = styled.div`
  width: 680px;
  height: 120px;
  margin: 24px 40px 0 40px;
  padding-top: 49px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.background._3};
  border: 1px dashed ${(props) => props.theme.border.greyDark};
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.text._2};
  text-align: center;
`

const TargetAreaWrapper = styled.div`
  width: 100%;
  min-height: 120px;
  margin-top: 24px;
`

const Title = styled.div`
  width: 100%;
  min-height: 32px;
  text-align: center;
  font-size: 24px;
  font-weight: 400;
  color: ${(props) => props.theme.text._1};
`

const Description = styled.div`
  width: 100%;
  min-height: 22px;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  color: ${(props) => props.theme.text._3};
`

const FormWrapper = styled.div`
  width: 100%;
  & .active-item-wrapper,
  .bottom-area {
    box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.1),
      0px 0px 8px 0px rgba(21, 29, 41, 0.02),
      0px 6px 8px 0px rgba(21, 29, 41, 0.08),
      inset 4px 0px 0px 0px ${(props) => props.theme.brand._1};
    .bottom-area {
      display: flex;
    }
    .dragdot-icon {
      display: block;
    }
  }
`
